import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  SimpleGrid,
  Card,
  CardBody,
  useColorMode,
  Icon,
  Flex,
} from "@chakra-ui/react";
import {
  FaUsers,
  FaCalendarAlt,
  FaShoppingCart,
  FaFileInvoiceDollar,
  FaTasks,
  FaEnvelope,
  FaPhone,
  FaChartLine,
} from "react-icons/fa";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor, getBrandValue } from "../brandConfig";
const modules = [
  { name: "Clients", icon: FaUsers, color: "#CB6BE6" },
  { name: "Calendar", icon: FaCalendarAlt, color: "#FFA500" },
  { name: "Products", icon: FaShoppingCart, color: "#32CD32" },
  { name: "Billing", icon: FaFileInvoiceDollar, color: "#FFD700" },
  { name: "Projects", icon: FaTasks, color: "#FF4500" },
  { name: "Email", icon: FaEnvelope, color: "#CB6BE6" },
  { name: "Phone", icon: FaPhone, color: "#FFA500" },
  { name: "Analytics", icon: FaChartLine, color: "#32CD32" },
];

const Home = () => {
  const { colorMode } = useColorMode();

  const bg = getColor("background.main", colorMode);
  const textPrimary = getColor(
    colorMode === "light" ? "text.primary" : "text.primaryDark",
    colorMode
  );
  const textSecondary = getColor(
    colorMode === "light" ? "text.secondary" : "text.secondaryDark",
    colorMode
  );
  const primary = getColor("primary", colorMode);

  return (
    <Box bg={bg} minH="100vh">
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box
        bg="linear-gradient(135deg, #1A202C 0%, #2D3748 50%, #1A202C 100%)"
        pt={{ base: 24, md: 32 }}
        pb={{ base: 16, md: 24 }}
        px={4}
      >
        <Container maxW="container.lg">
          <VStack spacing={6} textAlign="center">
            <Text
              fontSize="sm"
              fontWeight="bold"
              letterSpacing="widest"
              color="#CB6BE6"
              textTransform="uppercase"
            >
              VANGUARD IQ
            </Text>

            <Heading
              fontSize={{ base: "3xl", md: "5xl" }}
              fontWeight="bold"
              color="white"
              lineHeight="shorter"
            >
              VanguardiQ Back Office
            </Heading>

            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="whiteAlpha.800"
              maxW="700px"
              lineHeight="tall"
            >
              Welcome to the VanguardiQ collaboration platform. This is our back
              office management system where staff and active partners can login
              to track business operations and collaborate together.
            </Text>

            <Text
              fontSize={{ base: "sm", md: "md" }}
              color="whiteAlpha.600"
              maxW="600px"
            >
              If you're here, you're one of our team members or trusted
              partners. Access your tools below to manage our business
              intelligence platform.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Module Cards Section */}
      <Container maxW="container.lg" py={{ base: 12, md: 20 }}>
        <VStack spacing={8}>
          <Heading
            fontSize={{ base: "xl", md: "2xl" }}
            color={textPrimary}
            textAlign="center"
          >
            Our Business Modules
          </Heading>

          <SimpleGrid
            columns={{ base: 2, md: 4 }}
            spacing={{ base: 4, md: 6 }}
            w="full"
          >
            {modules.map((mod) => (
              <Card
                key={mod.name}
                bg={colorMode === "light" ? "white" : "#2D3748"}
                border="1px solid"
                borderColor={
                  colorMode === "light"
                    ? "rgba(203, 107, 230, 0.15)"
                    : "rgba(203, 107, 230, 0.3)"
                }
                borderRadius="xl"
                overflow="hidden"
                transition="all 0.2s"
                _hover={{
                  transform: "translateY(-2px)",
                  shadow: "0 8px 30px rgba(203, 107, 230, 0.15)",
                }}
              >
                <CardBody py={6} px={4} textAlign="center">
                  <Flex
                    w={12}
                    h={12}
                    mx="auto"
                    mb={3}
                    align="center"
                    justify="center"
                    borderRadius="lg"
                    bg={`${mod.color}15`}
                  >
                    <Icon as={mod.icon} boxSize={6} color={mod.color} />
                  </Flex>
                  <Text
                    fontWeight="semibold"
                    fontSize="sm"
                    color={textPrimary}
                  >
                    {mod.name}
                  </Text>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </VStack>
      </Container>

      {/* CTA Section */}
      <Box
        bg={colorMode === "light" ? "#FFFEF8" : "#2D3748"}
        py={{ base: 12, md: 16 }}
      >
        <Container maxW="container.md" textAlign="center">
          <VStack spacing={6}>
            <Heading
              fontSize={{ base: "xl", md: "2xl" }}
              color={textPrimary}
            >
              Ready to Get Started?
            </Heading>
            <Text color={textSecondary} maxW="500px">
              Login to access your business management dashboard and collaborate
              with your team.
            </Text>
            <Button
              size="lg"
              bg={primary}
              color="white"
              _hover={{ bg: "#B355D9" }}
              borderRadius="full"
              px={10}
            >
              Login
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* Footer Info */}
      <Box
        bg={colorMode === "light" ? "#FFFEF8" : "#1A202C"}
        py={8}
        borderTop="1px solid"
        borderColor={
          colorMode === "light"
            ? "rgba(203, 107, 230, 0.1)"
            : "rgba(203, 107, 230, 0.2)"
        }
      >
        <Container maxW="container.lg" textAlign="center">
          <VStack spacing={4}>
            <Text
              fontSize="sm"
              fontWeight="bold"
              letterSpacing="widest"
              color={primary}
              textTransform="uppercase"
            >
              VANGUARD IQ
            </Text>
            <Text fontSize="sm" color={textSecondary} maxW="600px">
              VanguardiQ streamlines business operations through digital
              automation. We empower clients with efficient, human-focused
              solutions that enhance productivity and drive success.
            </Text>
            <Text fontSize="xs" color={textSecondary}>
              {getBrandValue("contact.businessType")} | Technology |{" "}
              {getBrandValue("contact.email")}
            </Text>
            <Text fontSize="xs" color="#FFA500" fontWeight="semibold">
              Business Maintenance Plan - $97/mo
            </Text>
            <Text fontSize="xs" color={textSecondary}>
              &copy; 2026 Manage.VanguardiQ.com.au. All rights reserved.
            </Text>
            <Flex gap={4} fontSize="xs">
              <Text color={primary} cursor="pointer">
                Privacy
              </Text>
              <Text color={primary} cursor="pointer">
                Terms
              </Text>
              <Text color={primary} cursor="pointer">
                Contact
              </Text>
            </Flex>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default Home;
